"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { getAssistantRecordPath } from "@agroproj/backend/convex/assistant/routeContext";
import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import type { Route } from "next";
import Link from "next/link";
import { Component, useEffect, useState, type ReactNode } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConvexAuthReady } from "@/hooks/use-convex-auth-ready";
import { formatCurrencyBRL, formatNumber } from "@/lib/format";
class ProposalBoundary extends Component<{
  children: ReactNode;
}, {
  failed: boolean;
  message: string;
}> {
  state = {
    failed: false,
    message: "Não foi possível acessar esta revisão. Peça uma nova proposta."
  };
  static getDerivedStateFromError(cause: unknown) {
    return {
      failed: true,
      message: cause instanceof ConvexError && typeof cause.data === "string" ? cause.data : "Não foi possível acessar esta revisão. Peça uma nova proposta."
    };
  }
  render() {
    return this.state.failed ? <p role="status">{this.state.message}</p> : this.props.children;
  }
}
export function AssistantProjectConfirmation(t0) {
  const $ = _c(5);
  const {
    proposalId
  } = t0;
  let t1;
  if ($[0] !== proposalId) {
    t1 = <ProjectReview proposalId={proposalId} />;
    $[0] = proposalId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== proposalId || $[3] !== t1) {
    t2 = <ProposalBoundary key={proposalId}>{t1}</ProposalBoundary>;
    $[2] = proposalId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function ProjectReview({
  proposalId
}: {
  proposalId: string;
}) {
  const id = proposalId as Id<"assistant_project_proposals">;
  const {
    canQuery
  } = useConvexAuthReady();
  const proposal = useQuery(api.assistant.projects.getProposal, canQuery ? {
    proposalId: id
  } : "skip");
  const confirm = useMutation(api.assistant.projects.confirm);
  const cancel = useMutation(api.assistant.projects.cancel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiredAt, setExpiredAt] = useState<number>();
  const deadline = proposal?.status === "pending" ? proposal.expiresAt : undefined;
  useEffect(() => {
    if (deadline === undefined) return;
    const timeout = setTimeout(() => setExpiredAt(deadline), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timeout);
  }, [deadline]);
  async function submit(action: "confirm" | "cancel") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (action === "confirm") await confirm({
        proposalId: id,
        confirmed: true
      });else await cancel({
        proposalId: id
      });
    } catch (cause) {
      setError(cause instanceof ConvexError && typeof cause.data === "string" ? cause.data : "Não foi possível concluir. Tente novamente.");
    } finally {
      setBusy(false);
    }
  }
  if (!proposal) return <p role="status">Carregando revisão...</p>;
  if (proposal.status === "confirmed" && proposal.projectId) {
    const path = getAssistantRecordPath({
      organizationSlug: proposal.organizationSlug,
      recordType: "project",
      recordId: proposal.projectId
    });
    return <div role="status" className="space-y-2">
        <p>Projeto {proposal.type === "create" ? "criado" : "duplicado"}.</p>
        <Link className="underline underline-offset-4" href={path as Route}>
          Abrir projeto
        </Link>
      </div>;
  }
  if (proposal.status === "canceled") return <p role="status">Proposta cancelada.</p>;
  if (proposal.expired || expiredAt === proposal.expiresAt) return <p role="status">Proposta expirada. Peça uma nova revisão.</p>;
  const details = [...proposal.details, ...(proposal.type === "duplicate" && proposal.budget ? [{
    label: "Orçamento",
    value: `${proposal.budget.supplies} ${proposal.budget.supplies === 1 ? "insumo" : "insumos"} · ${proposal.budget.services} ${proposal.budget.services === 1 ? "serviço" : "serviços"} · ${proposal.budget.investments} ${proposal.budget.investments === 1 ? "investimento" : "investimentos"}`
  }, {
    label: "Total sem ASTEC",
    value: formatCurrencyBRL(proposal.budget.total)
  }, {
    label: "Recursos próprios",
    value: formatCurrencyBRL(proposal.budget.ownResources)
  }, {
    label: "Financiado",
    value: `${formatCurrencyBRL(proposal.budget.financed)}${proposal.budget.astecFinanced ? " (inclui ASTEC)" : ""}`
  }, {
    label: "ASTEC",
    value: `${formatNumber(proposal.budget.astecPercent, {
      maximumFractionDigits: 2
    })}% · ${proposal.budget.astecFinanced ? "financiada" : "não financiada"}`
  }] : [])];
  return <section aria-label="Revisão do projeto" className="mt-2 space-y-3 border-t pt-3">
      <p className="font-medium">Revise antes de confirmar</p>
      <dl className="space-y-1">
        {details.map(({
        label,
        value
      }) => <div key={label} className="flex gap-3">
            <dt className="text-muted-foreground w-24 shrink-0">{label}</dt>
            <dd className="min-w-0 break-words">{value}</dd>
          </div>)}
      </dl>
      {proposal.type === "duplicate" ? <p>
          Configuração e orçamento serão copiados. Documentos, propriedades, CAR e histórico
          permanecem no original.
        </p> : null}
      {error ? <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" type="button" variant="ghost" disabled={busy} onClick={() => void submit("cancel")}>
          Cancelar
        </Button>
        <Button size="sm" type="button" disabled={busy} onClick={() => void submit("confirm")}>
          {busy ? "Processando..." : proposal.type === "create" ? "Criar projeto" : "Duplicar projeto"}
        </Button>
      </div>
    </section>;
}